/**
 * L'écran Affectation — qui conduit quoi, et avec quelle remorque.
 *
 * Conforme à la maquette validée le 03/09/2026, en deux vues que l'utilisateur
 * a demandées l'une après l'autre :
 *
 *   - **VIGNETTES** : une miniature par véhicule, puis le grand schéma de celui
 *     qu'on choisit. Chaque pièce attelée porte sa date et une croix pour la
 *     détacher ; chaque place vide se remplit depuis la colonne des libres ;
 *   - **LISTE** : le même parc en tableau, avec des cases à cocher. Deux
 *     véhicules cochés font apparaître la bande d'échange — « on ajoute la
 *     possibilité de faire un changement rapide entre deux ressources ».
 *
 * UNE SEULE DIVERGENCE AVEC LA MAQUETTE, et elle est délibérée. La maquette
 * montrait trois colonnes de libres — véhicules, remorques, chauffeurs — pour
 * composer une combinaison à partir de rien. Il n'y a plus de colonne des
 * véhicules libres : le véhicule se choisit par sa vignette, et le schéma
 * affiche SON attelage. C'est ce que l'utilisateur a demandé en voyant la
 * première maquette — « il n'y a aucune notion des affectations en cours » — et
 * une colonne qui redemanderait le véhicule déjà choisi ferait deux chemins
 * pour la même chose.
 *
 * Découpe habituelle : `VueAffectation` dessine, `EcranAffectation` tient
 * l'état et appelle. Le calcul est dans `affectation.ts`, en fonctions pures.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  atteler,
  changerAttelage,
  echangerAttelages,
  lireAffectations,
  lireIndisponibilites,
  lireParc,
  type EchecApi,
  type FiltreAffectations,
} from './api.js';
import {
  attelageDe,
  attelages,
  aujourdhui,
  disponibilites,
  enFrancais,
  finProposee,
  lendemain,
  libres,
  refusFin,
  periodeDite,
  refusEchange,
  resumeEmpeches,
  resumeGenreLibre,
  resumeLibres,
  titreEchange,
  veille,
  type Affectation,
  type Attelage,
  type Indisponibilite,
  type Quoi,
} from './affectation.js';
import { nommer, type LigneParc } from './parc.js';
import { PARC_CHAUFFEURS, PARC_REMORQUES, PARC_VEHICULES } from './parcs.js';
import { SchemaAttelage, VignetteAttelage } from './SchemaAttelage.js';

type Vue = 'vignettes' | 'liste';

type EtatParc =
  | { readonly nom: 'chargement' }
  | {
      readonly nom: 'pret';
      readonly vehicules: readonly LigneParc[];
      readonly remorques: readonly LigneParc[];
      readonly chauffeurs: readonly LigneParc[];
      readonly affectations: readonly Affectation[];
      readonly indisponibilites: readonly Indisponibilite[];
    }
  | { readonly nom: 'panne' };

/** Ce que l'API a refusé, dit en français. */
const REFUS: Readonly<Record<EchecApi, string>> = {
  acces: 'Votre accès a été refusé. Reconnectez-vous.',
  introuvable: 'Cet attelage n’existe plus. Il a pu être défait entre-temps.',
  injoignable: 'Stafa ne répond pas. Vérifiez votre connexion et réessayez.',
  code_pris: 'Cette valeur est déjà prise.',
  invalide:
    'Une date n’est pas acceptée : le dernier jour d’attelage ne peut pas précéder le premier.',
  conflit:
    'Cette ressource est déjà attelée sur tout ou partie de cette période. Décrochez-la d’abord, ou choisissez d’autres dates.',
  encore_actif: 'Désactivez d’abord.',
  encore_rattache: 'Impossible : quelque chose s’y rattache encore.',
  // Les quatre refus du planning. Ils n'arrivent pas sur cet écran — la carte
  // doit rester exhaustive, c'est ce qui garantit qu'aucun refus de l'API ne
  // s'affichera un jour sous forme de code brut.
  ordre_annule: 'Cet ordre est annulé : il ne se planifie pas.',
  ordre_sans_etape: 'Cet ordre n’a aucune étape : rien à poser sur un camion.',
  etape_validee:
    'Une étape de cet ordre est déjà validée : ses dates ne se déplacent plus.',
  jour_sans_etape: 'Cet ordre n’a aucune étape ce jour-là. Rechargez l’écran.',
  // Ne vient jamais d'ici. La carte reste exhaustive, comme les autres.
  etape_en_tournee: 'Une étape de cet ordre appartient à une tournée.',
  litige_deja_ouvert:
    'Cette étape a déjà un litige ouvert : résolvez-le avant d’en ouvrir un autre.',
  litige_deja_resolu:
    'Ce litige est déjà résolu. Ouvrez-en un nouveau sur l’étape si l’incident se répète.',
};

// ---------------------------------------------------------------------------
// Le dessin
// ---------------------------------------------------------------------------

export interface ProprietesVueAffectation {
  readonly etat: EtatParc;
  readonly vue: Vue;
  readonly quandVue: (vue: Vue) => void;
  /** La date à laquelle on regarde, et à laquelle un changement prend effet. */
  readonly jour: string;
  readonly quandJour: (jour: string) => void;
  readonly vehiculeChoisi: string | null;
  readonly quandVehicule: (id: string) => void;
  readonly coches: readonly string[];
  readonly quandCocher: (id: string, coche: boolean) => void;
  readonly quandToutDecocher: () => void;
  readonly quandEchanger: (quoi: Quoi) => void;
  readonly quandAtteler: (genre: 'chauffeur' | 'remorque', ressourceId: string) => void;
  readonly quandDetacher: (affectationId: string) => void;
  /** Ouvre la demande d'attelage depuis une case vide du tableau. */
  readonly quandDemanderAttelage: (
    vehicule: LigneParc,
    genre: 'chauffeur' | 'remorque',
  ) => void;
  readonly envoi: boolean;
  readonly refus: string | null;
  /** Ce qui vient d'être fait, dit une fois. */
  readonly confirmation: string | null;
  readonly quandConfirmationLue: () => void;
  /**
   * L'HISTORIQUE, qui répond à « qui conduisait ce camion le 12 août ».
   *
   * Il est demandé à l'API plutôt que filtré sur ce qu'on a déjà : la liste
   * chargée pour dessiner le parc ne porte que les 500 dernières périodes, et
   * un historique cherché sur une fenêtre ancienne n'y serait pas.
   */
  readonly historique: Historique;
  readonly quandHistorique: (filtre: Partial<FiltreHistorique>) => void;
}

/** Ce qu'on cherche : une ressource, une fenêtre. */
export interface FiltreHistorique {
  /** « v:<id> », « c:<id> », « r:<id> », ou '' pour tout le parc. */
  readonly ressource: string;
  readonly du: string;
  readonly au: string;
}

export type Historique =
  | { readonly nom: 'ferme' }
  | { readonly nom: 'chargement'; readonly filtre: FiltreHistorique }
  | {
      readonly nom: 'pret';
      readonly filtre: FiltreHistorique;
      readonly lignes: readonly Affectation[];
    }
  | { readonly nom: 'panne'; readonly filtre: FiltreHistorique };

export function VueAffectation({
  etat,
  vue,
  quandVue,
  jour,
  quandJour,
  vehiculeChoisi,
  quandVehicule,
  coches,
  quandCocher,
  quandToutDecocher,
  quandEchanger,
  quandAtteler,
  quandDetacher,
  quandDemanderAttelage,
  envoi,
  refus,
  confirmation,
  quandConfirmationLue,
  historique,
  quandHistorique,
}: ProprietesVueAffectation) {
  if (etat.nom === 'chargement') {
    return (
      <div className="corps">
        <section className="liste">
          <p className="attente">Chargement du parc…</p>
        </section>
      </div>
    );
  }

  if (etat.nom === 'panne') {
    return (
      <div className="corps">
        <section className="liste">
          <div className="vide">
            <p>Stafa ne répond pas. Vérifiez votre connexion et réessayez.</p>
          </div>
        </section>
      </div>
    );
  }

  const liste = attelages(etat.vehicules, etat.affectations, jour);
  const choisi =
    vehiculeChoisi === null ? undefined : attelageDe(liste, vehiculeChoisi);

  const etatsRemorques = disponibilites(
    etat.remorques,
    etat.affectations,
    etat.indisponibilites,
    'remorque_id',
    jour,
  );
  const etatsChauffeurs = disponibilites(
    etat.chauffeurs,
    etat.affectations,
    etat.indisponibilites,
    'chauffeur_id',
    jour,
  );

  const cochesAttelages = coches
    .map((id) => attelageDe(liste, id))
    .filter((a): a is Attelage => a !== undefined);
  const [premier, second] = cochesAttelages;

  return (
    <div className="corps">
      <section className="liste">
        <div className="barre-outils barre-affectation">
          <label className="champ-date">
            <span className="etiquette">
              Attelages au{' '}
              <span className="sourdine">· pour consulter une autre date</span>
            </span>
            <input
              className="champ"
              type="date"
              value={jour}
              disabled={envoi}
              onChange={(e) => quandJour(e.target.value)}
            />
          </label>
          <div className="bascule-vue" role="group" aria-label="Forme d’affichage">
            <button
              className={vue === 'liste' ? 'bouton' : 'bouton bouton-discret'}
              type="button"
              aria-pressed={vue === 'liste'}
              onClick={() => quandVue('liste')}
            >
              Tableau
            </button>
            <button
              className={vue === 'vignettes' ? 'bouton' : 'bouton bouton-discret'}
              type="button"
              aria-pressed={vue === 'vignettes'}
              onClick={() => quandVue('vignettes')}
            >
              Vignettes
            </button>
          </div>
        </div>

        {refus !== null && <p className="bandeau">{refus}</p>}

        {confirmation !== null && (
          <p className="bandeau bandeau-fait" role="status">
            {confirmation}
            <button
              className="fermer-bandeau"
              type="button"
              aria-label="Fermer"
              onClick={quandConfirmationLue}
            >
              ×
            </button>
          </p>
        )}

        {liste.length === 0 && (
          <div className="vide">
            <p>Aucun véhicule actif dans le parc.</p>
          </div>
        )}

        {vue === 'vignettes' && liste.length > 0 && (
          <VueVignettes
            liste={liste}
            choisi={choisi}
            quandVehicule={quandVehicule}
            jour={jour}
            etatsRemorques={etatsRemorques}
            etatsChauffeurs={etatsChauffeurs}
            quandAtteler={quandAtteler}
            quandDetacher={quandDetacher}
            envoi={envoi}
          />
        )}

        {vue === 'liste' && liste.length > 0 && (
          <VueListe
            liste={liste}
            coches={coches}
            quandCocher={quandCocher}
            quandToutDecocher={quandToutDecocher}
            quandEchanger={quandEchanger}
            quandDetacher={quandDetacher}
            quandDemanderAttelage={quandDemanderAttelage}
            premier={premier}
            second={second}
            jour={jour}
            envoi={envoi}
          />
        )}

        <Historique
          etat={historique}
          quandFiltre={quandHistorique}
          vehicules={etat.vehicules}
          remorques={etat.remorques}
          chauffeurs={etat.chauffeurs}
        />
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// L'historique
// ---------------------------------------------------------------------------

/**
 * « Qui conduisait ce camion le 12 août ? »
 *
 * Replié par défaut : c'est une recherche, pas un tableau de bord, et déplié il
 * pousserait le schéma hors de l'écran.
 *
 * **Une fenêtre garde ce qu'elle CHEVAUCHE**, non ce qui y tient entièrement —
 * une période à cheval sur le 1er avril concerne bien avril. C'est l'API qui en
 * décide ; l'écran ne fait que passer les deux bornes.
 */
function Historique({
  etat,
  quandFiltre,
  vehicules,
  remorques,
  chauffeurs,
}: {
  readonly etat: Historique;
  readonly quandFiltre: (filtre: Partial<FiltreHistorique>) => void;
  readonly vehicules: readonly LigneParc[];
  readonly remorques: readonly LigneParc[];
  readonly chauffeurs: readonly LigneParc[];
}) {
  if (etat.nom === 'ferme') {
    return (
      <div className="cadre-historique">
        <button
          className="bouton bouton-discret"
          type="button"
          onClick={() => quandFiltre({})}
        >
          Historique des attelages
        </button>
      </div>
    );
  }

  const { filtre } = etat;

  return (
    <div className="cadre-historique ouvert">
      <div className="colonne-titre">Historique des attelages</div>

      <div className="barre-outils barre-affectation">
        <label className="champ-date">
          <span className="etiquette">Ressource</span>
          <select
            className="champ"
            value={filtre.ressource}
            onChange={(e) => quandFiltre({ ressource: e.target.value })}
          >
            <option value="">Tout le parc</option>
            <optgroup label="Véhicules">
              {vehicules.map((v) => (
                <option value={`v:${v.id}`} key={v.id}>
                  {nommer(v, PARC_VEHICULES)}
                </option>
              ))}
            </optgroup>
            <optgroup label="Remorques">
              {remorques.map((r) => (
                <option value={`r:${r.id}`} key={r.id}>
                  {nommer(r, PARC_REMORQUES)}
                </option>
              ))}
            </optgroup>
            <optgroup label="Chauffeurs">
              {chauffeurs.map((c) => (
                <option value={`c:${c.id}`} key={c.id}>
                  {nommer(c, PARC_CHAUFFEURS)}
                </option>
              ))}
            </optgroup>
          </select>
        </label>

        <label className="champ-date">
          <span className="etiquette">Entre le</span>
          <input
            className="champ"
            type="date"
            value={filtre.du}
            onChange={(e) => quandFiltre({ du: e.target.value })}
          />
        </label>

        <label className="champ-date">
          <span className="etiquette">et le</span>
          <input
            className="champ"
            type="date"
            value={filtre.au}
            onChange={(e) => quandFiltre({ au: e.target.value })}
          />
        </label>
      </div>

      {etat.nom === 'chargement' && <p className="attente">Recherche…</p>}

      {etat.nom === 'panne' && (
        <p className="empechement">
          Stafa ne répond pas. Vérifiez votre connexion et réessayez.
        </p>
      )}

      {etat.nom === 'pret' && etat.lignes.length === 0 && (
        <p className="empechement">Aucun attelage sur cette période.</p>
      )}

      {etat.nom === 'pret' && etat.lignes.length > 0 && (
        <>
          <p className="decompte">
            {etat.lignes.length} période{etat.lignes.length > 1 ? 's' : ''} trouvée
            {etat.lignes.length > 1 ? 's' : ''}
          </p>
          <div className="tableau-cadre">
            <table>
              <thead>
                <tr>
                  <th scope="col">Véhicule</th>
                  <th scope="col" className="col-large">
                    Genre
                  </th>
                  <th scope="col">Ressource</th>
                  <th scope="col">Période</th>
                </tr>
              </thead>
              <tbody>
                {etat.lignes.map((ligne) => (
                  <tr className="rangee" key={ligne.id}>
                    <td className="code">{ligne.vehicule}</td>
                    <td className="sourdine col-large">{ligne.genre}</td>
                    <td className={ligne.genre === 'remorque' ? 'code' : undefined}>
                      {ligne.ressource}
                    </td>
                    <td>{periodeDite(ligne)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// La vue en vignettes
// ---------------------------------------------------------------------------

function VueVignettes({
  liste,
  choisi,
  quandVehicule,
  jour,
  etatsRemorques,
  etatsChauffeurs,
  quandAtteler,
  quandDetacher,
  envoi,
}: {
  readonly liste: readonly Attelage[];
  readonly choisi: Attelage | undefined;
  readonly quandVehicule: (id: string) => void;
  readonly jour: string;
  readonly etatsRemorques: ReturnType<typeof disponibilites>;
  readonly etatsChauffeurs: ReturnType<typeof disponibilites>;
  readonly quandAtteler: (genre: 'chauffeur' | 'remorque', ressourceId: string) => void;
  readonly quandDetacher: (affectationId: string) => void;
  readonly envoi: boolean;
}) {
  const remorquesLibres = libres(etatsRemorques);
  const chauffeursLibres = libres(etatsChauffeurs);

  return (
    <>
      <div className="bande-vignettes" role="tablist" aria-label="Le parc">
        {liste.map((attelage) => {
          const actif = choisi?.vehicule.id === attelage.vehicule.id;
          return (
            <button
              className={actif ? 'vignette choisie' : 'vignette'}
              type="button"
              role="tab"
              aria-selected={actif}
              key={attelage.vehicule.id}
              onClick={() => quandVehicule(attelage.vehicule.id)}
            >
              <VignetteAttelage
                vehicule={nommer(attelage.vehicule, PARC_VEHICULES)}
                remorque={attelage.remorque?.ressource ?? null}
                chauffeur={attelage.chauffeur?.ressource ?? null}
              />
              <span className="vignette-nom">
                {nommer(attelage.vehicule, PARC_VEHICULES)}
              </span>
              {/*
                Les TROIS lignes, toujours. La version précédente n'écrivait le
                chauffeur et la remorque que lorsqu'ils MANQUAIENT : une vignette
                complète ne montrait que l'immatriculation du tracteur, et il
                fallait cliquer pour savoir qui conduisait. Relevé par
                l'utilisateur le 04/09/2026 — « les vignettes n'affichent que le
                tracteur affecté ». La date est celle du plus RÉCENT des deux
                attelages, comme la colonne « Attelé depuis » de la vue liste :
                deux calculs différents pour la même chose finiraient par
                diverger.
              */}
              <span
                className={
                  attelage.chauffeur === undefined
                    ? 'vignette-ligne vignette-manque'
                    : 'vignette-ligne'
                }
              >
                {attelage.chauffeur?.ressource ?? 'sans chauffeur'}
              </span>
              <span
                className={
                  attelage.remorque === undefined
                    ? 'vignette-ligne vignette-manque'
                    : 'vignette-ligne'
                }
              >
                {attelage.remorque?.ressource ?? 'sans remorque'}
              </span>
              {(() => {
                const debuts = [attelage.chauffeur?.du, attelage.remorque?.du].filter(
                  (d): d is string => d !== undefined,
                );
                if (debuts.length === 0) return null;
                return (
                  <span className="vignette-depuis">
                    depuis le {enFrancais(debuts.sort().at(-1) as string)}
                  </span>
                );
              })()}
            </button>
          );
        })}
      </div>

      {choisi !== undefined && (
        <>
          <SchemaAttelage
            vehicule={nommer(choisi.vehicule, PARC_VEHICULES)}
            remorque={choisi.remorque?.ressource ?? null}
            chauffeur={choisi.chauffeur?.ressource ?? null}
            depuisRemorque={enFrancais(choisi.remorque?.du ?? null)}
            depuisChauffeur={enFrancais(choisi.chauffeur?.du ?? null)}
            {...(choisi.remorque === undefined || envoi
              ? {}
              : {
                  quandDetacherRemorque: () =>
                    quandDetacher((choisi.remorque as Affectation).id),
                })}
            {...(choisi.chauffeur === undefined || envoi
              ? {}
              : {
                  quandDetacherChauffeur: () =>
                    quandDetacher((choisi.chauffeur as Affectation).id),
                })}
          />

          {(choisi.remorque === undefined || choisi.chauffeur === undefined) && (
            <>
              <p className="decompte">
                {resumeLibres(remorquesLibres.length, chauffeursLibres.length, jour)}
                {(() => {
                  const dits = [
                    resumeEmpeches(etatsRemorques, (r) => nommer(r, PARC_REMORQUES)),
                    resumeEmpeches(etatsChauffeurs, (c) => nommer(c, PARC_CHAUFFEURS)),
                  ].filter((d) => d !== '');
                  return dits.length === 0 ? '' : ` — ${dits.join(', ')}`;
                })()}
              </p>

              <div className="colonnes-libres">
                {choisi.remorque === undefined && (
                  <ColonneLibres
                    titre="Remorques libres"
                    ressources={remorquesLibres}
                    parc={PARC_REMORQUES}
                    envoi={envoi}
                    quandChoisir={(id) => quandAtteler('remorque', id)}
                  />
                )}
                {choisi.chauffeur === undefined && (
                  <ColonneLibres
                    titre="Chauffeurs libres"
                    ressources={chauffeursLibres}
                    parc={PARC_CHAUFFEURS}
                    envoi={envoi}
                    quandChoisir={(id) => quandAtteler('chauffeur', id)}
                  />
                )}
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}

function ColonneLibres({
  titre,
  ressources,
  parc,
  envoi,
  quandChoisir,
}: {
  readonly titre: string;
  readonly ressources: readonly LigneParc[];
  readonly parc: typeof PARC_REMORQUES;
  readonly envoi: boolean;
  readonly quandChoisir: (id: string) => void;
}) {
  return (
    <div className="colonne-libres">
      <div className="colonne-titre">{titre}</div>
      {ressources.length === 0 ? (
        <p className="empechement">Rien de libre à cette date.</p>
      ) : (
        ressources.map((ressource) => (
          <button
            className="ligne-libre"
            type="button"
            key={ressource.id}
            disabled={envoi}
            onClick={() => quandChoisir(ressource.id)}
          >
            {nommer(ressource, parc)}
          </button>
        ))
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// La vue en liste, et l'échange
// ---------------------------------------------------------------------------

function VueListe({
  liste,
  coches,
  quandCocher,
  quandToutDecocher,
  quandEchanger,
  quandDetacher,
  quandDemanderAttelage,
  premier,
  second,
  jour,
  envoi,
}: {
  readonly liste: readonly Attelage[];
  readonly coches: readonly string[];
  readonly quandCocher: (id: string, coche: boolean) => void;
  readonly quandToutDecocher: () => void;
  readonly quandEchanger: (quoi: Quoi) => void;
  readonly quandDetacher: (affectationId: string) => void;
  readonly quandDemanderAttelage: (
    vehicule: LigneParc,
    genre: 'chauffeur' | 'remorque',
  ) => void;
  readonly premier: Attelage | undefined;
  readonly second: Attelage | undefined;
  readonly jour: string;
  readonly envoi: boolean;
}) {
  return (
    <>
      <p className="decompte">
        {liste.length} véhicule{liste.length > 1 ? 's' : ''}
        {coches.length > 0 &&
          ` — ${coches.length} coché${coches.length > 1 ? 's' : ''}`}
      </p>

      {coches.length === 2 && premier !== undefined && second !== undefined && (
        <BandeEchange
          premier={premier}
          second={second}
          jour={jour}
          envoi={envoi}
          quandEchanger={quandEchanger}
          quandToutDecocher={quandToutDecocher}
        />
      )}

      <div className="tableau-cadre">
        <table>
          <thead>
            <tr>
              <th scope="col" className="col-case">
                <span className="sourdine">—</span>
              </th>
              <th scope="col">Véhicule</th>
              <th scope="col" className="col-large">
                Type
              </th>
              <th scope="col">Chauffeur</th>
              <th scope="col">Remorque</th>
              <th scope="col" className="col-large">
                Attelé depuis
              </th>
            </tr>
          </thead>
          <tbody>
            {liste.map((attelage) => {
              const id = attelage.vehicule.id;
              const coche = coches.includes(id);
              // La date affichée est celle du plus RÉCENT des deux attelages :
              // c'est le dernier changement, et c'est ce qu'on cherche.
              const debuts = [attelage.chauffeur?.du, attelage.remorque?.du].filter(
                (d): d is string => d !== undefined,
              );
              const depuis =
                debuts.length === 0 ? null : enFrancais(debuts.sort().at(-1) as string);

              return (
                <tr className={coche ? 'rangee choisie' : 'rangee'} key={id}>
                  <td className="col-case">
                    <input
                      type="checkbox"
                      checked={coche}
                      disabled={envoi || (!coche && coches.length >= 2)}
                      aria-label={`Choisir ${nommer(attelage.vehicule, PARC_VEHICULES)}`}
                      onChange={(e) => quandCocher(id, e.target.checked)}
                    />
                  </td>
                  <td className="code">{nommer(attelage.vehicule, PARC_VEHICULES)}</td>
                  <td className="col-large">
                    {(attelage.vehicule['type'] as string | null) ?? (
                      <span className="sourdine">non renseigné</span>
                    )}
                  </td>
                  {/*
                    L'action est SOUS la ressource, et toujours là. Avant le
                    04/09/2026, « atteler » n'existait que dans les vignettes et
                    seulement si la place était vide : un camion complet
                    n'offrait plus rien, et rien ne laissait deviner qu'il
                    fallait d'abord détacher.
                  */}
                  <td>
                    {attelage.chauffeur === undefined ? (
                      <>
                        <span className="sourdine">sans chauffeur</span>
                        <button
                          className="action-cellule"
                          type="button"
                          disabled={envoi}
                          onClick={() =>
                            quandDemanderAttelage(attelage.vehicule, 'chauffeur')
                          }
                        >
                          Atteler un chauffeur
                        </button>
                      </>
                    ) : (
                      <>
                        <span>{attelage.chauffeur.ressource}</span>
                        <button
                          className="action-cellule risque"
                          type="button"
                          disabled={envoi}
                          onClick={() =>
                            quandDetacher((attelage.chauffeur as Affectation).id)
                          }
                        >
                          Décrocher
                        </button>
                      </>
                    )}
                  </td>
                  <td>
                    {attelage.remorque === undefined ? (
                      <>
                        <span className="sourdine">sans remorque</span>
                        <button
                          className="action-cellule"
                          type="button"
                          disabled={envoi}
                          onClick={() =>
                            quandDemanderAttelage(attelage.vehicule, 'remorque')
                          }
                        >
                          Atteler une remorque
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="code">{attelage.remorque.ressource}</span>
                        <button
                          className="action-cellule risque"
                          type="button"
                          disabled={envoi}
                          onClick={() =>
                            quandDetacher((attelage.remorque as Affectation).id)
                          }
                        >
                          Décrocher
                        </button>
                      </>
                    )}
                  </td>
                  {depuis === null ? (
                    <td className="sourdine col-large">—</td>
                  ) : (
                    <td className="col-large">{depuis}</td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function BandeEchange({
  premier,
  second,
  jour,
  envoi,
  quandEchanger,
  quandToutDecocher,
}: {
  readonly premier: Attelage;
  readonly second: Attelage;
  readonly jour: string;
  readonly envoi: boolean;
  readonly quandEchanger: (quoi: Quoi) => void;
  readonly quandToutDecocher: () => void;
}) {
  const boutons: readonly { readonly quoi: Quoi; readonly nom: string }[] = [
    { quoi: 'remorques', nom: 'Échanger les remorques' },
    { quoi: 'chauffeurs', nom: 'Échanger les chauffeurs' },
    { quoi: 'les_deux', nom: 'Échanger les deux' },
  ];

  return (
    <div className="bande-echange">
      <span className="echange-titre">
        {titreEchange(premier, second, (v) => nommer(v, PARC_VEHICULES))}
      </span>
      <span className="echange-date">
        À partir du {enFrancais(jour) ?? jour}
        <span className="sourdine">
          {' '}
          · les attelages en place seront clos le {enFrancais(veille(jour)) ?? ''}
        </span>
      </span>
      <div className="actions">
        {boutons.map(({ quoi, nom }, rang) => {
          const refus = refusEchange(premier, second, quoi, jour);
          return (
            <button
              className={rang === 0 ? 'bouton' : 'bouton bouton-discret'}
              type="button"
              key={quoi}
              disabled={envoi || refus !== null}
              title={refus ?? undefined}
              onClick={() => quandEchanger(quoi)}
            >
              {nom}
            </button>
          );
        })}
        <button
          className="bouton bouton-discret"
          type="button"
          disabled={envoi}
          onClick={quandToutDecocher}
        >
          Tout décocher
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// L'état
// ---------------------------------------------------------------------------

export interface ProprietesAffectation {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
}

export function EcranAffectation({ jeton, quandAccesPerdu }: ProprietesAffectation) {
  const [etat, setEtat] = useState<EtatParc>({ nom: 'chargement' });
  // Le TABLEAU par défaut depuis le 04/09/2026. Les vignettes montraient un
  // seul ensemble à la fois sur toute la largeur de l'écran ; l'exploitant en a
  // sept et veut les voir tous le matin. Les vignettes restent, en second.
  const [vue, setVue] = useState<Vue>('liste');
  const [jour, setJour] = useState(aujourdhui());
  const [vehiculeChoisi, setVehiculeChoisi] = useState<string | null>(null);
  const [coches, setCoches] = useState<readonly string[]>([]);
  const [envoi, setEnvoi] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);
  /**
   * Ce qu'on vient de faire, dit une fois.
   *
   * Posé le 04/09/2026 : décrocher au jour même ne changeait RIEN à l'écran —
   * la fin est ce jour-là inclus, donc l'attelage le couvre encore — et on
   * cliquait sans rien voir se passer. Le bandeau dit ce qui a eu lieu et à
   * partir de quand ça se verra.
   */
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [historique, setHistorique] = useState<Historique>({ nom: 'ferme' });
  /**
   * Le décrochage en cours, s'il y en a un.
   *
   * Décrocher DEMANDE la date depuis le 04/09/2026, à la demande de
   * l'utilisateur. Auparavant, l'écran posait la fin à la veille du jour
   * affiché en haut, sans rien demander : décrocher le jour même une remorque
   * attelée le jour même était refusé par la base, et le seul contournement
   * était d'aller changer une date qui, elle, ne sert qu'à consulter.
   */
  const [decrochage, setDecrochage] = useState<Decrochage | null>(null);
  /**
   * L'attelage en cours de composition, s'il y en a un.
   *
   * Ouvert depuis le TABLEAU, où chaque case vide porte son « Atteler ». La
   * date s'y saisit comme au décrochage : sans elle, on ne pourrait atteler
   * qu'au jour affiché en haut, qui ne sert qu'à consulter.
   */
  const [attelage, setAttelage] = useState<Attellement | null>(null);

  const avecJeton = useCallback(async (): Promise<string | null> => {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }, [jeton, quandAccesPerdu]);

  /**
   * Tout est chargé d'un coup, à l'ouverture : les trois parcs, les attelages
   * et les arrêts. Sans filtre de date sur les affectations — on change de date
   * en tapant, et redemander l'API à chaque frappe ferait clignoter l'écran
   * pour un calcul que le navigateur fait en une milliseconde.
   */
  const charger = useCallback(async () => {
    const j = await avecJeton();
    if (j === null) return;

    const [v, r, c, a, i] = await Promise.all([
      lireParc(j, PARC_VEHICULES),
      lireParc(j, PARC_REMORQUES),
      lireParc(j, PARC_CHAUFFEURS),
      lireAffectations(j),
      lireIndisponibilites(j),
    ]);

    if (
      v.etat !== 'ok' ||
      r.etat !== 'ok' ||
      c.etat !== 'ok' ||
      a.etat !== 'ok' ||
      i.etat !== 'ok'
    ) {
      for (const resultat of [v, r, c, a, i]) {
        if (resultat.etat === 'echec' && resultat.cause === 'acces') quandAccesPerdu();
      }
      setEtat({ nom: 'panne' });
      return;
    }

    setEtat({
      nom: 'pret',
      vehicules: v.valeur,
      remorques: r.valeur,
      chauffeurs: c.valeur,
      affectations: a.valeur,
      indisponibilites: i.valeur,
    });
    setVehiculeChoisi((actuel) => {
      if (actuel !== null && v.valeur.some((ligne) => ligne.id === actuel)) {
        return actuel;
      }
      return v.valeur.find((ligne) => ligne.actif)?.id ?? null;
    });
  }, [avecJeton, quandAccesPerdu]);

  useEffect(() => {
    void charger();
  }, [charger]);

  /** Une écriture, puis un rechargement : la base a le dernier mot. */
  const ecrire = (
    action: (j: string) => Promise<{ etat: 'ok' } | { etat: 'echec'; cause: EchecApi }>,
    dit?: string,
  ) => {
    setEnvoi(true);
    setRefus(null);
    setConfirmation(null);

    void (async () => {
      const j = await avecJeton();
      if (j === null) {
        setEnvoi(false);
        return;
      }
      const resultat = await action(j);
      if (resultat.etat === 'ok') {
        await charger();
        setCoches([]);
        setEnvoi(false);
        if (dit !== undefined) setConfirmation(dit);
        return;
      }
      if (resultat.cause === 'acces') quandAccesPerdu();
      // Chaque cause a son message, et un seul. La version précédente affichait
      // le message de CONFLIT pour tout `invalide` : une fin antérieure au début
      // s'annonçait donc comme un recouvrement de périodes. Constaté sur le
      // poste le 04/09/2026.
      setRefus(REFUS[resultat.cause]);
      setEnvoi(false);
    })();
  };

  const ecran = (
    <VueAffectation
      etat={etat}
      vue={vue}
      quandVue={(v) => {
        setVue(v);
        setCoches([]);
      }}
      jour={jour}
      quandJour={(j) => {
        // Un champ de date vidé rendrait la chaîne vide, et tout deviendrait
        // « non couvert ». On garde alors la date précédente.
        if (j !== '') setJour(j);
      }}
      confirmation={confirmation}
      quandConfirmationLue={() => setConfirmation(null)}
      quandDemanderAttelage={(vehicule, genre) => {
        setRefus(null);
        setConfirmation(null);
        setAttelage({
          vehiculeId: vehicule.id,
          vehicule: nommer(vehicule, PARC_VEHICULES),
          genre,
          du: jour,
          ressourceId: '',
        });
      }}
      vehiculeChoisi={vehiculeChoisi}
      quandVehicule={setVehiculeChoisi}
      coches={coches}
      quandCocher={(id, coche) =>
        setCoches((actuels) =>
          coche
            ? actuels.includes(id)
              ? actuels
              : [...actuels, id].slice(0, 2)
            : actuels.filter((c) => c !== id),
        )
      }
      quandToutDecocher={() => setCoches([])}
      quandEchanger={(quoi) => {
        const [a, b] = coches;
        if (a === undefined || b === undefined) return;
        ecrire((j) =>
          echangerAttelages(j, { vehicule_a: a, vehicule_b: b, quoi, du: jour }),
        );
      }}
      quandAtteler={(genre, ressourceId) => {
        if (vehiculeChoisi === null) return;
        ecrire((j) =>
          atteler(j, {
            vehicule_id: vehiculeChoisi,
            ...(genre === 'chauffeur'
              ? { chauffeur_id: ressourceId }
              : { remorque_id: ressourceId }),
            du: jour,
          }),
        );
      }}
      quandDetacher={(affectationId) => {
        // On n'écrit plus rien ici : on OUVRE la demande de date. C'est elle
        // qui écrira, avec la borne saisie.
        if (etat.nom !== 'pret') return;
        const ligne = etat.affectations.find((a) => a.id === affectationId);
        if (ligne === undefined) return;
        setRefus(null);
        setDecrochage({
          id: ligne.id,
          du: ligne.du,
          genre: ligne.genre,
          ressource: ligne.ressource,
          vehicule: ligne.vehicule,
          au: finProposee(ligne.du, jour),
        });
      }}
      envoi={envoi}
      refus={refus}
      historique={historique}
      quandHistorique={(change) => {
        // Ouvrir sans filtre : les trois derniers mois, tout le parc. Une
        // fenêtre par défaut plutôt qu'aucune — sans elle, la première réponse
        // serait les 500 dernières périodes du parc entier, ce qui n'est une
        // réponse à rien.
        const depart: FiltreHistorique =
          historique.nom === 'ferme'
            ? { ressource: '', du: ilYaTroisMois(jour), au: jour }
            : historique.filtre;
        const filtre = { ...depart, ...change };
        setHistorique({ nom: 'chargement', filtre });

        void (async () => {
          const j = await avecJeton();
          if (j === null) return;
          const lu = await lireAffectations(j, filtreApi(filtre));
          if (lu.etat === 'ok') {
            setHistorique({ nom: 'pret', filtre, lignes: lu.valeur });
          } else {
            if (lu.cause === 'acces') quandAccesPerdu();
            setHistorique({ nom: 'panne', filtre });
          }
        })();
      }}
    />
  );

  return (
    <>
      {ecran}
      {attelage !== null && etat.nom === 'pret' && (
        <BoiteAttelage
          attelage={attelage}
          libres={libres(
            disponibilites(
              attelage.genre === 'chauffeur' ? etat.chauffeurs : etat.remorques,
              etat.affectations,
              etat.indisponibilites,
              attelage.genre === 'chauffeur' ? 'chauffeur_id' : 'remorque_id',
              attelage.du,
            ),
          )}
          envoi={envoi}
          quandChanger={(champs) => setAttelage({ ...attelage, ...champs })}
          quandFermer={() => setAttelage(null)}
          quandValider={() => {
            const { vehiculeId, genre, du, ressourceId } = attelage;
            if (ressourceId === '') return;
            setAttelage(null);
            ecrire(
              (j) =>
                atteler(j, {
                  vehicule_id: vehiculeId,
                  ...(genre === 'chauffeur'
                    ? { chauffeur_id: ressourceId }
                    : { remorque_id: ressourceId }),
                  du,
                }),
              `Attelé à partir du ${enFrancais(du) ?? du}.`,
            );
          }}
        />
      )}
      {decrochage !== null && (
        <BoiteDecrochage
          decrochage={decrochage}
          envoi={envoi}
          quandAu={(au) => setDecrochage({ ...decrochage, au })}
          quandFermer={() => setDecrochage(null)}
          quandValider={() => {
            const { id, du, au, ressource, genre } = decrochage;
            if (refusFin(du, au) !== null) return;
            setDecrochage(null);
            ecrire(
              (j) => changerAttelage(j, id, { au }),
              `${ressource} est décroché${genre === 'remorque' ? 'e' : ''} : ${
                genre === 'remorque' ? 'elle' : 'il'
              } redevient libre le ${enFrancais(lendemain(au)) ?? lendemain(au)}.`,
            );
          }}
        />
      )}
    </>
  );
}

/** Ce que la demande d'attelage tient le temps d'être remplie. */
interface Attellement {
  readonly vehiculeId: string;
  /** L'immatriculation du véhicule, pour le titre. */
  readonly vehicule: string;
  readonly genre: 'chauffeur' | 'remorque';
  /** Le premier jour d'attelage, tel qu'il est saisi. */
  readonly du: string;
  /** La ressource choisie. Vide tant que rien n'est choisi. */
  readonly ressourceId: string;
}

/** Ce que la demande de date tient le temps d'être remplie. */
interface Decrochage {
  readonly id: string;
  /** Le premier jour de l'attelage : la fin ne peut pas le précéder. */
  readonly du: string;
  readonly genre: 'chauffeur' | 'remorque';
  /** Le nom du chauffeur, ou l'immatriculation de la remorque. */
  readonly ressource: string;
  readonly vehicule: string;
  /** Le dernier jour d'attelage, tel qu'il est saisi. */
  readonly au: string;
}

/**
 * La demande d'attelage : une date, puis une ressource libre à cette date.
 *
 * **La liste se recalcule quand la date change**, et c'est le point de tout
 * l'écran : une remorque libre aujourd'hui peut être prise lundi. Une liste
 * figée au jour affiché en haut ferait proposer des ressources que la base
 * refuserait ensuite.
 */
function BoiteAttelage({
  attelage,
  libres: ressources,
  envoi,
  quandChanger,
  quandFermer,
  quandValider,
}: {
  readonly attelage: Attellement;
  readonly libres: readonly LigneParc[];
  readonly envoi: boolean;
  readonly quandChanger: (champs: Partial<Attellement>) => void;
  readonly quandFermer: () => void;
  readonly quandValider: () => void;
}) {
  const parc = attelage.genre === 'chauffeur' ? PARC_CHAUFFEURS : PARC_REMORQUES;
  const quoi = attelage.genre === 'chauffeur' ? 'un chauffeur' : 'une remorque';

  return (
    <div className="voile-boite" role="presentation">
      <div className="boite-decrochage" role="dialog" aria-modal="true">
        <h3>Atteler {quoi}</h3>
        <p className="quoi-decroche">Au véhicule {attelage.vehicule}.</p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            quandValider();
          }}
        >
          <label className="etiquette-boite" htmlFor="debut-attelage">
            Premier jour d’attelage
          </label>
          <input
            id="debut-attelage"
            className="champ-boite"
            type="date"
            value={attelage.du}
            disabled={envoi}
            onChange={(e) => quandChanger({ du: e.target.value, ressourceId: '' })}
          />

          <p className="etiquette-boite etiquette-liste">
            {resumeGenreLibre(ressources.length, attelage.genre, attelage.du)}
          </p>

          {ressources.length === 0 ? (
            <p className="faute-boite">
              Rien de libre à cette date. Décrochez d’abord, ou choisissez un autre
              jour.
            </p>
          ) : (
            <div className="choix-libres">
              {ressources.map((ressource) => (
                <button
                  className={
                    ressource.id === attelage.ressourceId
                      ? 'ligne-libre retenue'
                      : 'ligne-libre'
                  }
                  type="button"
                  key={ressource.id}
                  disabled={envoi}
                  aria-pressed={ressource.id === attelage.ressourceId}
                  onClick={() => quandChanger({ ressourceId: ressource.id })}
                >
                  {nommer(ressource, parc)}
                </button>
              ))}
            </div>
          )}

          <div className="pied-boite">
            <button
              className="bouton"
              type="submit"
              disabled={envoi || attelage.ressourceId === ''}
            >
              {envoi ? 'Enregistrement…' : 'Atteler'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={envoi}
              onClick={quandFermer}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * La demande de date, posée par-dessus l'écran.
 *
 * **« Dernier jour d'attelage » et non « décroché le »**, décidé le 04/09/2026.
 * La borne saisie est EXACTEMENT celle qui part en base : aucun calcul de veille
 * dans le dos, donc rien à deviner sur ce que la date veut dire. C'est ce calcul
 * silencieux qui rendait le décrochage du jour même impossible.
 */
function BoiteDecrochage({
  decrochage,
  envoi,
  quandAu,
  quandFermer,
  quandValider,
}: {
  readonly decrochage: Decrochage;
  readonly envoi: boolean;
  readonly quandAu: (au: string) => void;
  readonly quandFermer: () => void;
  readonly quandValider: () => void;
}) {
  const faute = refusFin(decrochage.du, decrochage.au);
  const quoi = decrochage.genre === 'chauffeur' ? 'le chauffeur' : 'la remorque';

  return (
    <div className="voile-boite" role="presentation">
      <div className="boite-decrochage" role="dialog" aria-modal="true">
        <h3>Décrocher {quoi}</h3>
        <p className="quoi-decroche">
          <strong>{decrochage.ressource}</strong> du véhicule {decrochage.vehicule},
          attelé depuis le {enFrancais(decrochage.du) ?? decrochage.du}.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            quandValider();
          }}
        >
          <label className="etiquette-boite" htmlFor="fin-attelage">
            Dernier jour d’attelage
          </label>
          <input
            id="fin-attelage"
            className="champ-boite"
            type="date"
            value={decrochage.au}
            min={decrochage.du}
            disabled={envoi}
            onChange={(e) => quandAu(e.target.value)}
          />
          {faute === null ? (
            <p className="aide-boite">
              Ce jour compris. {decrochage.genre === 'chauffeur' ? 'Il' : 'Elle'} sera
              de nouveau libre le lendemain, et l’attelage restera dans l’historique.
            </p>
          ) : (
            <p className="faute-boite">{faute}</p>
          )}
          <div className="pied-boite">
            <button className="bouton" type="submit" disabled={envoi || faute !== null}>
              {envoi ? 'Enregistrement…' : 'Décrocher'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={envoi}
              onClick={quandFermer}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Le filtre de l'écran, traduit pour l'API.
 *
 * La ressource y est portée par un préfixe — « v: », « r: », « c: » — parce
 * qu'un `<select>` ne rend qu'une chaîne, et que l'API attend trois paramètres
 * distincts. Un seul endroit fait la traduction.
 */
function filtreApi(filtre: FiltreHistorique): FiltreAffectations {
  const [genre, id] = filtre.ressource.split(':');
  return {
    ...(filtre.du === '' ? {} : { du: filtre.du }),
    ...(filtre.au === '' ? {} : { au: filtre.au }),
    ...(id === undefined || id === ''
      ? {}
      : genre === 'v'
        ? { vehicule: id }
        : genre === 'r'
          ? { remorque: id }
          : { chauffeur: id }),
  };
}

/**
 * Trois mois en arrière, en `AAAA-MM-JJ`.
 *
 * Calculé en UTC, comme `veille` : on n'en tire qu'une date, jamais une heure.
 * `setUTCMonth` ramène de lui-même le 31 mai à trois mois en arrière au 28
 * février — c'est le comportement voulu, et il vaut mieux que d'inventer une
 * règle de bord de mois.
 */
function ilYaTroisMois(jour: string): string {
  const morceaux = /^(\d{4})-(\d{2})-(\d{2})$/.exec(jour);
  if (morceaux === null) return jour;
  const date = new Date(
    Date.UTC(Number(morceaux[1]), Number(morceaux[2]) - 1, Number(morceaux[3])),
  );
  date.setUTCMonth(date.getUTCMonth() - 3);
  return date.toISOString().slice(0, 10);
}
